import React from 'react'

const CompanyCard = ({ company }) => {
  return (
    <a
      href={company.website}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      <div className="glass-card rounded-2xl p-6 hover:scale-105 transition-all duration-300 group animate-float relative cursor-pointer">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-200 transition-colors">
              {company.name}
            </h3>
            <div className="flex items-center space-x-4 text-sm text-white/70">
              <div className="flex items-center space-x-1">
                {/* Location Icon */}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{company.headquarters}</span>
              </div>
              <div className="flex items-center space-x-1">
                {/* Founded Icon */}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v9a1 1 0 01-1 1H5a1 1 0 01-1-1V8a1 1 0 011-1h3z" />
                </svg>
                <span>Founded {company.founded}</span>
              </div>
            </div>
          </div>
          
          {/* 🔴 Remove this anchor tag completely to avoid nesting */}
          {/* You can replace the icon with a static element if needed */}
          <div
            className="p-2 rounded-lg bg-white/10 text-white/80 transition-all duration-300 pointer-events-none"
            title={`Visit ${company.name} website`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        </div>

      {/* Expertise Areas */}
      <div className="space-y-3 mb-4">
        <h4 className="text-white/90 font-semibold text-sm flex items-center space-x-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span>Areas of Expertise</span>
        </h4>
        <div className="flex flex-wrap gap-2">
          {company.expertise.map((area, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white/90 text-sm rounded-lg border border-white/20 backdrop-blur-sm"
            >
              {area}
            </span>
          ))}
        </div>
      </div>

      {/* Famous Products */}
      <div className="space-y-3">
        <h4 className="text-white/90 font-semibold text-sm flex items-center space-x-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span>Famous Products</span>
        </h4>
        <div className="grid grid-cols-1 gap-2">
          {company.famousProducts.slice(0, 4).map((product, index) => (
            <div
              key={index}
              className="px-3 py-2 bg-gradient-to-r from-orange-500/15 to-pink-500/15 text-white/90 text-sm rounded-lg border border-orange-500/20 backdrop-blur-sm hover:from-orange-500/25 hover:to-pink-500/25 transition-all duration-300 flex items-center space-x-2"
            >
              <div className="w-1.5 h-1.5 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full flex-shrink-0"></div>
              <span className="truncate">{product}</span>
            </div>
          ))}
          {company.famousProducts.length > 4 && (
            <div className="px-3 py-2 bg-gradient-to-r from-gray-500/15 to-gray-600/15 text-white/70 text-sm rounded-lg border border-gray-500/20 backdrop-blur-sm text-center">
              +{company.famousProducts.length - 4} more products
            </div>
          )}
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-4 right-4 w-16 h-16 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
      
      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
    </div>
    </a>
  )
}

export default CompanyCard
